import {RmCalendarEntryType} from '../enums/calendar-enums';

export type ApiDateOnly = string;
export type ApiDateTimeUtc = string;

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME_PATTERN = /^(\d{4}-\d{2}-\d{2})T/;
const EXPLICIT_TIME_ZONE_PATTERN = /(Z|[+-]\d{2}:?\d{2})$/i;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface ApiCalendarRangeLike {
    startUtc: string;
    endUtc: string;
    isAllDay?: boolean;
    type?: RmCalendarEntryType;
}

/**
 * Global API date boundary helpers.
 * Use yyyy-MM-dd strings for business calendar days and ISO UTC strings for real instants.
 * Do not construct date-only values through new Date('yyyy-MM-dd'), because browsers parse that as UTC.
 */
export class ApiDateAdapter {
    static parseInstant(value: string | Date): Date {
        if (value instanceof Date) return new Date(value.getTime());
        return new Date(value);
    }

    static parseDateOnly(value: string | Date): Date {
        if (value instanceof Date)
            return this.localDay(value);

        const dateOnlyValue = this.toDateOnlyKey(value);
        const [year, month, day] = dateOnlyValue.split('-').map(Number);
        return new Date(year, month - 1, day, 0, 0, 0, 0);
    }

    static isDateOnly(value?: string | null): boolean {
        return !!value && DATE_ONLY_PATTERN.test(value);
    }

    static toDateOnlyKey(value: string | Date): ApiDateOnly {
        if (value instanceof Date) return this.formatLocalDateOnly(value);

        if (this.isDateOnly(value)) return value;

        const dateTimeMatch = DATE_TIME_PATTERN.exec(value);
        if (dateTimeMatch != null && !EXPLICIT_TIME_ZONE_PATTERN.test(value))
            return dateTimeMatch[1];

        const instant = new Date(value);
        if (!Number.isNaN(instant.getTime())) {
            return [
                instant.getUTCFullYear(),
                this.pad2(instant.getUTCMonth() + 1),
                this.pad2(instant.getUTCDate()),
            ].join('-');
        }

        return value.substring(0, 10);
    }

    static toDateTimeUtc(value: Date): ApiDateTimeUtc {
        return value.toISOString();
    }

    static calendarRangeStart(range: ApiCalendarRangeLike): Date {
        return this.isDateOnlyRange(range)
            ? this.parseDateOnly(range.startUtc)
            : this.parseInstant(range.startUtc);
    }

    static calendarEntryStart(range: ApiCalendarRangeLike): Date {
        return this.calendarRangeStart(range);
    }

    static calendarRangeEnd(range: ApiCalendarRangeLike, fallbackStart?: Date): Date {
        if (!this.isDateOnlyRange(range)) return this.parseInstant(range.endUtc);

        const start = fallbackStart ?? this.parseDateOnly(range.startUtc);
        const apiEnd = this.parseDateOnly(range.endUtc);

        // Persisted absence entries use an inclusive business end date. Virtual
        // public holidays use an exclusive end date so a one-day holiday is
        // represented as [day, next day). Keep the distinction at the API
        // boundary instead of globally shifting every all-day entry.
        if (range.type !== RmCalendarEntryType.PublicHoliday) {
            return apiEnd.getTime() < start.getTime() ? new Date(start) : apiEnd;
        }

        const inclusiveEnd = new Date(apiEnd.getTime() - MS_PER_DAY);
        return inclusiveEnd.getTime() < start.getTime() ? new Date(start) : inclusiveEnd;
    }

    static calendarEntryEnd(range: ApiCalendarRangeLike, fallbackStart?: Date): Date {
        return this.calendarRangeEnd(range, fallbackStart);
    }

    static calendarRangeFormStart(range: ApiCalendarRangeLike): Date {
        return this.calendarRangeStart(range);
    }

    static calendarRangeFormEnd(range: ApiCalendarRangeLike): Date {
        return this.calendarRangeEnd(range);
    }

    static isRangeOnLocalDay(range: ApiCalendarRangeLike, day: Date): boolean {
        const localDay = this.localDay(day);
        const start = this.calendarRangeStart(range);
        const end = this.calendarRangeEnd(range, start);
        return start.getTime() <= localDay.getTime() && end.getTime() >= localDay.getTime();
    }

    static rangeDurationMs(range: ApiCalendarRangeLike): number {
        if (!this.isDateOnlyRange(range)) {
            const start = this.parseInstant(range.startUtc).getTime();
            const end = this.parseInstant(range.endUtc).getTime();
            return Math.max(0, end - start);
        }

        const start = this.parseDateOnly(range.startUtc).getTime();
        const end = this.parseDateOnly(range.endUtc).getTime();
        const raw = end - start;

        return range.type === RmCalendarEntryType.PublicHoliday
            ? Math.max(0, raw)
            : Math.max(0, raw + MS_PER_DAY);
    }

    static localDay(value: Date): Date {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 0, 0, 0, 0);
    }

    static isSameLocalDay(left: Date, right: Date): boolean {
        return this.localDay(left).getTime() === this.localDay(right).getTime();
    }

    static buildUtcYearRange(year: number): { fromUtcIso: ApiDateTimeUtc; toUtcIso: ApiDateTimeUtc } {
        return {
            fromUtcIso: new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0)).toISOString(),
            toUtcIso: new Date(Date.UTC(year + 1, 0, 1, 0, 0, 0, 0)).toISOString(),
        };
    }

    static isDateOnlyRange(range: ApiCalendarRangeLike): boolean {
        return this.isDateOnly(range.startUtc) || this.isDateOnly(range.endUtc);
    }

    private static formatLocalDateOnly(value: Date): ApiDateOnly {
        return [
            value.getFullYear(),
            this.pad2(value.getMonth() + 1),
            this.pad2(value.getDate()),
        ].join('-');
    }

    private static pad2(value: number): string {
        return value.toString().padStart(2, '0');
    }
}

export const parseApiInstant = ApiDateAdapter.parseInstant.bind(ApiDateAdapter);
export const parseApiDateOnly = ApiDateAdapter.parseDateOnly.bind(ApiDateAdapter);
export const toApiDateOnlyKey = ApiDateAdapter.toDateOnlyKey.bind(ApiDateAdapter);
export const toApiDateTimeUtc = ApiDateAdapter.toDateTimeUtc.bind(ApiDateAdapter);
export const calendarEntryStartsOnLocalDate = ApiDateAdapter.calendarRangeStart.bind(ApiDateAdapter);
export const calendarEntryEndsOnLocalDate = ApiDateAdapter.calendarRangeEnd.bind(ApiDateAdapter);
export const calendarEntryFormStart = ApiDateAdapter.calendarRangeFormStart.bind(ApiDateAdapter);
export const calendarEntryFormEnd = ApiDateAdapter.calendarRangeFormEnd.bind(ApiDateAdapter);
export const isCalendarEntryOnLocalDay = ApiDateAdapter.isRangeOnLocalDay.bind(ApiDateAdapter);
export const calendarEntryDurationMs = ApiDateAdapter.rangeDurationMs.bind(ApiDateAdapter);
export const normalizeLocalDay = ApiDateAdapter.localDay.bind(ApiDateAdapter);
export const isSameLocalDay = ApiDateAdapter.isSameLocalDay.bind(ApiDateAdapter);

export const parseCalendarInstant = parseApiInstant;
